import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const UserModal = ({ show, user, agencies, carriers, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    userType: '',
    full_name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    role: '',
    agency_id: '',
    carrier_id: '',
    is_active: true
  });

  // Kullanıcı düzenleme durumunda form verilerini doldur
  useEffect(() => {
    if (user) {
      setFormData({
        userType: user.entity_type || 'system',
        full_name: user.full_name || '',
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        password: '',
        role: user.role || '',
        agency_id: user.entity_id && user.entity_type === 'agency' ? user.entity_id : '',
        carrier_id: user.entity_id && user.entity_type === 'carrier' ? user.entity_id : '',
        is_active: user.is_active !== undefined ? user.is_active : true
      });
    } else {
      // Yeni kullanıcı oluşturma
      setFormData({
        userType: '',
        full_name: '',
        username: '',
        email: '',
        phone: '',
        password: '',
        role: '',
        agency_id: '',
        carrier_id: '',
        is_active: true
      });
    }
  }, [user]);

  const handleUserTypeChange = (e) => {
    const newUserType = e.target.value;
    setFormData(prev => ({
      ...prev,
      userType: newUserType,
      role: '',
      agency_id: '',
      carrier_id: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const submitData = {
        full_name: formData.full_name,
        username: formData.username,
        email: formData.email,
        phone: formData.phone,
        is_active: formData.is_active
      };

      // Şifre sadece yeni kullanıcı oluştururken gönderilir
      if (!user && formData.password) {
        submitData.password = formData.password;
      }

      // Kullanıcı türüne göre ek alanlar
      if (formData.userType === 'system' || (!formData.userType && !user?.entity_type)) {
        submitData.role = formData.role;
      } else if (formData.userType === 'agency') {
        submitData.agency_id = formData.agency_id;
        submitData.entity_type = 'agency';
      } else if (formData.userType === 'carrier') {
        submitData.carrier_id = formData.carrier_id;
        submitData.entity_type = 'carrier';
      }

      await onSave(submitData);
    } catch (error) {
      console.error('Form gönderilirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Modal Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {user ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Oluştur'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Kullanıcı Türü Seçimi */}
            {!user && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kullanıcı Türü</label>
                <select 
                  value={formData.userType}
                  onChange={handleUserTypeChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Kullanıcı türü seçin</option>
                  <option value="system">Sistem Kullanıcısı</option>
                  <option value="agency">Acente Kullanıcısı</option>
                  <option value="carrier">Taşıyıcı Kullanıcısı</option>
                </select>
              </div>
            )}

            {/* Temel Bilgiler */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad *</label>
                <input
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  type="text"
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kullanıcı Adı *</label>
                <input
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  type="text"
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-posta *</label>
                <input
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  type="email"
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                <input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  type="tel"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Şifre */}
            {!user && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Şifre *</label>
                <input
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  type="password"
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {/* Sistem Kullanıcısı Rolü */}
            {(formData.userType === 'system' || (user && !user.entity_type)) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol *</label>
                <select 
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Rol seçin</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="operation">Operasyon</option>
                  <option value="finance">Finans</option>
                  <option value="agent">Acente Temsilcisi</option>
                  <option value="driver">Şoför</option>
                </select>
              </div>
            )}

            {/* Acente Seçimi */}
            {(formData.userType === 'agency' || (user && user.entity_type === 'agency')) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Acente *</label>
                <select 
                  value={formData.agency_id}
                  onChange={(e) => setFormData({...formData, agency_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Acente seçin</option>
                  {agencies.map(agency => (
                    <option key={agency.id} value={agency.id}>
                      {agency.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Taşıyıcı Seçimi */}
            {(formData.userType === 'carrier' || (user && user.entity_type === 'carrier')) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Taşıyıcı Firma *</label>
                <select 
                  value={formData.carrier_id}
                  onChange={(e) => setFormData({...formData, carrier_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Taşıyıcı firma seçin</option>
                  {carriers.map(carrier => (
                    <option key={carrier.id} value={carrier.id}>
                      {carrier.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Durum */}
            {user && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
                <select 
                  value={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.value === 'true'})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={true}>Aktif</option>
                  <option value={false}>Pasif</option>
                </select>
              </div>
            )}

            {/* Form Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Kaydediliyor...' : (user ? 'Güncelle' : 'Oluştur')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserModal;
